/**
 * Helpers for canonical path + query + fragment ordering.
 * Correct form: /path?utm_source=...#section
 */

/**
 * Split a path that may incorrectly embed ?query or #hash.
 * Returns { pathname, searchParams, hash } without leading ?/# on those parts.
 */
export function splitPathQueryHash(path = '/') {
  const raw = String(path || '/');
  let pathname = raw;
  let hash = '';
  let search = '';

  const hashIndex = pathname.indexOf('#');
  if (hashIndex >= 0) {
    hash = pathname.slice(hashIndex + 1);
    pathname = pathname.slice(0, hashIndex);
  }

  const queryIndex = pathname.indexOf('?');
  if (queryIndex >= 0) {
    search = pathname.slice(queryIndex + 1);
    pathname = pathname.slice(0, queryIndex);
  }

  // Guard against malformed "#section?utm_..." or "#section&utm_..."
  if (hash.includes('?') || hash.includes('&utm_') || /^[^?]*&utm_/.test(hash)) {
    const malformed = hash;
    const qInHash = malformed.indexOf('?');
    if (qInHash >= 0) {
      hash = malformed.slice(0, qInHash);
      const fromHash = malformed.slice(qInHash + 1);
      search = search ? `${search}&${fromHash}` : fromHash;
    } else if (malformed.includes('&utm_')) {
      const amp = malformed.indexOf('&utm_');
      hash = malformed.slice(0, amp);
      const fromHash = malformed.slice(amp + 1);
      search = search ? `${search}&${fromHash}` : fromHash;
    }
  }

  if (!pathname.startsWith('/')) pathname = `/${pathname}`;

  return {
    pathname,
    searchParams: new URLSearchParams(search),
    hash: hash.replace(/^#/, ''),
  };
}

/**
 * True when every query param appears before any fragment.
 * Also false for empty/invalid URLs or fragments that contain utm_ query syntax.
 */
export function hasQueryBeforeFragment(urlString) {
  const value = String(urlString || '');
  if (!value) return false;

  const hashIndex = value.indexOf('#');
  const queryIndex = value.indexOf('?');

  if (hashIndex >= 0) {
    const fragment = value.slice(hashIndex + 1);
    if (fragment.includes('?') || /(?:^|&)utm_/.test(fragment)) {
      return false;
    }
  }

  if (queryIndex >= 0 && hashIndex >= 0 && hashIndex < queryIndex) {
    return false;
  }

  return true;
}

/**
 * Build /path?query#hash with query always before fragment.
 */
export function buildPathWithQueryAndHash(pathname, searchParams = {}, hash = '') {
  const { pathname: cleanPath, searchParams: embedded, hash: embeddedHash } = splitPathQueryHash(pathname);
  const params = new URLSearchParams(embedded.toString());

  Object.entries(searchParams || {}).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, String(value));
  });

  const query = params.toString();
  const cleanHash = String(hash || embeddedHash || '').replace(/^#/, '');
  return `${cleanPath}${query ? `?${query}` : ''}${cleanHash ? `#${cleanHash}` : ''}`;
}

/** Alias redirect target for /services-and-pricing → canonical services page. */
export function buildServicesAliasRedirectTarget(search = '', hash = '') {
  const searchPart = search && search.startsWith('?') ? search : search ? `?${search}` : '';
  const hashPart = hash && hash.startsWith('#') ? hash : hash ? `#${hash}` : '';
  return buildPathWithQueryAndHash(`/financial-planning-services${searchPart}${hashPart}`);
}
